/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
  onMount,
} from "solid-js";

// import { ExpandAnimationWrapper } from "@/components/Base/AnimationWrapper";
import { Input } from "@/components/Base/Input/Input.tsx";
import Popup from "@/components/Base/Popup/Popup.tsx";
import Icon, { svgPathTypes } from "@/components/Icon.tsx";
import { useOnClickOutside } from "@/hooks/useClickOutside";
import { useI18n } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import { getProviderLogo } from "@/lib/resFolder";
import { cn } from "@/lib/tailwindcssTool";

interface Option {
  label: string;
  value: string | number;
  [key: string]: any;
}

interface SelectProps {
  options: Option[];
  label?: string;
  placeholder?: string;
  defaultValue?: string | number | (string | number)[];
  multiple?: boolean;
  class?: string;
  inputClass?: string;
  downClass?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  bgColor?:
    | "bg-general-bg2"
    | "bg-home-bg2"
    | "bg-general-enterbg"
    | "bg-general-bg3";
  isClickOutsideClose?: boolean;
  onChange?: (value: string | number | (string | number)[]) => void;
  showPrefix?: boolean;
  prefixText?: string;
  showSelectedPrefix?: boolean;
  selectedPrefixText?: string | JSX.Element;
  arrowIcon?: "general/arrow_down" | "under_arrow";
}

export default function Select(props: SelectProps) {
  const { t } = useI18n();
  const isMultiple = props.multiple ?? false;
  const [open, setOpen] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal("");
  const [filteredOptions, setFilteredOptions] = createSignal<Option[]>(
    props.options,
  );
  const [selected, setSelected] = createSignal<Option[]>(
    Array.isArray(props.defaultValue)
      ? props.options.filter((opt) =>
          (props.defaultValue as (string | number)[]).includes(opt.value),
        )
      : props.defaultValue !== undefined
        ? props.options.filter((opt) => opt.value === props.defaultValue) // 如果是单个值
        : [],
  );
  const [popupRef, setPopupRef] = createSignal<{ handleClose: () => void }>();

  const Prefix = (): JSX.Element => {
    return <Icon name="search" class="text-general-icon size-[14px]" />;
  };

  createMemo(() => {
    if (props.options) {
      if (isMultiple) {
        setSelected([]);
      } else if (props.defaultValue !== undefined) {
        // 当 options 或 defaultValue 改变时，重新设置选中值
        const newSelected = props.options.filter(
          (opt) => opt.value === props.defaultValue,
        );
        setSelected(newSelected);
      }
    }
  });

  // 添加对 defaultValue 的监听
  createEffect(
    on(
      createMemo(() => props.defaultValue !== undefined && !isMultiple),
      (boolean) => {
        if (boolean) {
          const newSelected = props.options.filter(
            (opt) => opt.value === props.defaultValue,
          );
          setSelected(newSelected);
        }
      },
    ),
  );

  const toggle = () => setOpen((prev) => !prev);

  const selectItem = (item: Option) => {
    if (isMultiple) {
      const current = selected();
      const exists = current.find((i) => i.value === item.value);
      const updated = exists
        ? current.filter((i) => i.value !== item.value)
        : [...current, item];

      setSelected(updated);
      props.onChange?.(updated.map((i) => i.value));
    } else {
      setSelected([item]);
      setOpen(false);
      props.onChange?.(item.value);
    }
  };

  // 清除所有选中项
  const onClearAll = () => {
    if (selected().length === 0) return;
    setSelected([]);
    props.onChange?.([]); // 清除后触发 onChange 确保原资料有被触发更新
  };

  let dropdownRef: HTMLElement;
  const PopupHeaderIcon = (): JSX.Element => {
    return (
      <div class="flex flex-row items-center justify-center gap-[12px]">
        <Show when={isMultiple} fallback={<></>}>
          <div
            class="bg-general-icondi flex h-[28px] min-w-[72px] items-center rounded-[6px] px-[8px] text-[14px] font-[500] leading-[17px]"
            classList={{
              "text-general-light": selected().length !== 0,
              "text-general-darkzi": selected().length === 0,
            }}
            onClick={onClearAll}
          >
            {t("search.clearall")}
          </div>
        </Show>
        <div
          class="icon-square-button size-[28px]"
          onClick={() => {
            if (popupRef()) {
              popupRef()?.handleClose();
            } else {
              setOpen(false);
            }
          }}
        >
          <Icon name="close" class="text-general-light size-[12px]" />
        </div>
      </div>
    );
  };

  onMount(() => {
    if (props.isClickOutsideClose ?? true) {
      useOnClickOutside(dropdownRef, () => setOpen(false));
    }
  });

  // 过滤器
  function filterOptions(options: Option[], query: string): Option[] {
    if (!query) return options;
    const lowerQuery = query.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerQuery),
    );
  }
  // 搜索时实时过滤
  createEffect(() => {
    setFilteredOptions(filterOptions(props.options, searchQuery()));
  });

  return (
    <div
      class={cn("relative w-full max-w-xs", props.class)}
      ref={(el) => (dropdownRef = el)}
    >
      {props.label && (
        <label class="mb-1 block text-sm text-gray-600">{props.label}</label>
      )}
      <div
        class={cn(
          "text-general-light flex h-full cursor-pointer items-center justify-between rounded border border-[var(--home-sk)] px-3 py-2 text-[13px]",
          props.bgColor || "bg-general-enterbg",
          props.inputClass,
        )}
        onclick={toggle}
      >
        <span class={selected().length ? "" : "text-general-light"}>
          {selected().length ? (
            isMultiple ? (
              <>
                {props.showPrefix && (
                  <span class="text-general-bright font-[500]">
                    {props.prefixText || t("home.providers") + ": "}
                  </span>
                )}

                <Show when={isMobile()} fallback={<></>}>
                  <span class="text-general-bright font-[500]">
                    {t("home.providers")}:
                  </span>
                  <span class="text-general-light font-[600]">
                    {" "}
                    +{selected().length}
                  </span>
                </Show>
                <Show when={!isMobile()} fallback={<></>}>
                  <span class="text-general-bright font-[500]">
                    {t("home.providers")}:
                  </span>
                  <span class="text-general-light font-[600]">
                    {" "}
                    +{selected().length}
                  </span>
                </Show>
              </>
            ) : (
              <>
                {props.showSelectedPrefix && (
                  <span class="text-general-bright font-[500] leading-[16px]">
                    {props.selectedPrefixText || t("home.sort_by") + ": "}
                  </span>
                )}
                <span class="text-general-light font-[600] leading-[16px]">
                  {selected()[0].label}
                </span>
              </>
            )
          ) : (
            <>
              {props.showSelectedPrefix ? (
                <span class="text-general-bright">
                  {props.selectedPrefixText || t("home.sort_by") + ": "}
                </span>
              ) : (
                <span class="text-general-darkzi">
                  {props.placeholder || "please select"}
                </span>
              )}
            </>
          )}
        </span>

        <Show
          when={props.arrowIcon}
          fallback={
            <span class="bg-general-icondi flex h-[22px] w-[22px] items-center justify-center rounded-[3.67px]">
              <Icon
                name={"general/arrow_down"}
                class={`text-general-light size-[8px] transition-transform ${
                  open() ? "rotate-180" : "rotate-0"
                }`}
              />
            </span>
          }
        >
          <Icon
            name={props.arrowIcon as svgPathTypes}
            class={`text-general-icon size-[12px] transition-transform ${
              open() ? "rotate-0" : "rotate-[-90deg]"
            }`}
          />
        </Show>
      </div>

      <Show
        when={isMobile()}
        fallback={
          <div
            class={cn("absolute z-50 w-full", props.downClass)}
            style={{
              "box-shadow": "0px 3px 10px 0px rgb(4,4,4,0.3)",
              "max-height": open() ? "377px" : "0px",
              opacity: open() ? "1" : "0",
              transform: open()
                ? "scale(1) translateY(0)"
                : "scale(0.95) translateY(-10px)",
              "transform-origin": "top center",
              transition: "max-height 0.3s ease-in-out",
              "pointer-events": open() ? "auto" : "none",
              overflow: "hidden",
            }}
          >
            <div
              class="pointer-events-auto fixed left-0 top-0 z-0 h-screen w-screen"
              onClick={() => setOpen(false)}
            />
            <div class="bg-general-bg2 text-general-light relative z-[1] mt-[4px] flex max-h-[377px] flex-col items-center justify-start overflow-hidden rounded-[8px] shadow">
              <Show when={props.showSearch} fallback={<></>}>
                <div class="w-full px-[12px] py-[10px]">
                  <Input
                    id="mobile-select-search"
                    name="mobile-select-search"
                    onInput={(e) => setSearchQuery(e.currentTarget.value)}
                    value={searchQuery()}
                    class="bg-general-bg1 h-[40px] p-[10px] text-[13px]"
                    placeholder={props.searchPlaceholder}
                    IsFloatPlaceholder={false}
                    prefix={Prefix()}
                    showClose
                    onClose={() => setSearchQuery("")}
                  />
                </div>
              </Show>
              <ul class="w-full flex-1 overflow-y-auto overscroll-contain">
                {filteredOptions().map((item) => {
                  const isChecked = selected().some(
                    (s) => s.value === item.value,
                  );
                  return (
                    <li
                      class={`hover:bg-general-bg3 flex h-[52px] cursor-pointer items-center gap-x-[10px] px-[20px] ${
                        isChecked ? "bg-general-bg3" : ""
                      }`}
                      onClick={() => selectItem(item)}
                    >
                      {isMultiple ? (
                        <>
                          <span
                            class={`flex h-4 w-4 items-center justify-center ${
                              isChecked
                                ? ""
                                : "border-general-icon rounded-[4px] border-[1px]"
                            }`}
                          >
                            {isChecked && (
                              <div class="flex h-[16px] w-[16px] cursor-pointer items-center justify-center rounded-[3.5px] bg-gradient-to-b from-[var(--general-main)] to-[var(--general-assist)]">
                                <Icon
                                  name="is_checked"
                                  class="h-[16px] w-[16px]"
                                />
                              </div>
                            )}
                          </span>
                          <span class="flex w-full flex-1 flex-row items-center justify-between">
                            <Image
                              src={getProviderLogo(item.logo)}
                              alt={item.name}
                              class="h-[28px] w-[100px]"
                            />
                            <Show when={item.game_count} fallback={<></>}>
                              <span class="text-general-darkzi text-[14px] font-medium">
                                {t("common.games_count", {
                                  val: item.game_count,
                                })}
                              </span>
                            </Show>
                          </span>
                        </>
                      ) : (
                        <div class="flex w-full items-center justify-between">
                          <span class="text-[14px] font-[500] leading-[16px]">
                            {item.label}
                          </span>
                          <Show
                            when={props.defaultValue == item.value}
                            fallback={<></>}
                          >
                            <Icon name="correct" class="size-[20px]" />
                          </Show>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <Show when={isMultiple} fallback={<></>}>
                <div
                  class="text-general-main border-general-sk mx-[18px] mt-[10px] flex h-[47px] w-[calc(100%_-_36px)] cursor-pointer flex-row items-center justify-center gap-x-[6px] rounded-b-[8px] border-t-[1px] border-solid pb-[20px] pt-[10px]"
                  onClick={onClearAll}
                >
                  <span class="text-[14px] font-semibold">
                    {t("search.clearall")}
                  </span>
                  <Icon name="right_arrow_double" class="h-[12px] w-[10px]" />
                </div>
              </Show>
            </div>
          </div>
        }
      >
        <Popup
          show={open()}
          onClose={() => setOpen(false)}
          title={t("common.select")}
          contentClass={`px-0 ${props.showSearch ? "h-[70vh]" : "h-full max-h-[70dvh]l"}`}
          typeFlag={false}
          headerIcon={PopupHeaderIcon()}
          ref={(ref) => {
            setPopupRef(ref);
          }}
        >
          <div
            class={cn(
              "bg-general-bg2 text-general-light flex w-full flex-col items-center justify-start",
              props.downClass,
            )}
          >
            <Show when={props.showSearch} fallback={<></>}>
              <div class="w-full px-[12px] pb-[10px]">
                <Input
                  id="mobile-select-search"
                  name="mobile-select-search"
                  onInput={(e) => setSearchQuery(e.currentTarget.value)}
                  value={searchQuery()}
                  class="bg-general-bg1 h-[40px] p-[10px]"
                  placeholder={props.searchPlaceholder}
                  IsFloatPlaceholder={false}
                  prefix={Prefix()}
                  showClose
                  onClose={() => setSearchQuery("")}
                />
              </div>
            </Show>
            <ul class="w-full flex-1 overflow-y-auto">
              {filteredOptions().map((item) => {
                const isChecked = selected().some(
                  (s) => s.value === item.value,
                );
                return (
                  <li
                    class={`hover:bg-general-bg3 flex h-[48px] cursor-pointer items-center gap-2 px-[20px] py-[10px] ${
                      isChecked ? "bg-general-bg3" : ""
                    }`}
                    onClick={() => {
                      selectItem(item);
                    }}
                  >
                    {isMultiple ? (
                      <div class="flex h-full w-full items-center gap-[20px]">
                        <span
                          class={`flex h-4 min-w-4 items-center justify-center ${
                            isChecked
                              ? ""
                              : "border-general-icon rounded-[4px] border-[1px]"
                          }`}
                        >
                          {isChecked && (
                            <div class="flex h-[16px] w-[16px] cursor-pointer items-center justify-center rounded-[3.5px] bg-gradient-to-b from-[var(--general-main)] to-[var(--general-assist)]">
                              <Icon name="is_checked" />
                            </div>
                          )}
                        </span>
                        <span class="text-general-darkzi flex h-full w-full items-center justify-between">
                          <Image
                            src={getProviderLogo(item.logo)}
                            alt={item.label}
                            class="h-[28px]"
                            textClass="text-general-light text-[14px] font-medium"
                          />
                          <Show when={item.game_count} fallback={<></>}>
                            {t("common.games_count", {
                              val: item.game_count,
                            })}
                          </Show>
                        </span>
                      </div>
                    ) : (
                      <div class="flex w-full items-center justify-between">
                        <span class="text-[14px] font-[500] leading-[16px]">
                          {item.label}
                        </span>
                        <Show
                          when={props.defaultValue == item.value}
                          fallback={<></>}
                        >
                          <Icon
                            name="correct"
                            class="text-general-main size-[20px]"
                          />
                        </Show>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </Popup>
      </Show>
    </div>
  );
}

const Image = (props: {
  src: string;
  alt: string;
  class?: string;
  textClass?: string;
}) => {
  const [imgSrc, setImgSrc] = createSignal(props.src);

  const handleError = () => {
    setImgSrc(""); // 清空 src，瀏覽器就不會顯示 broken image icon
  };

  return (
    <>
      {imgSrc() ? (
        <img
          src={imgSrc()}
          alt={props.alt}
          onError={handleError}
          class={props.class}
        />
      ) : (
        <span class={cn("text-general-light", props.textClass)}>
          {props.alt}
        </span>
      )}
    </>
  );
};
